import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const TOPICS = [
  "General question",
  "Billing / payments",
  "Audit results / accuracy",
  "Bug report",
  "Feature request",
  "Partnership / studio inquiry",
];

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and your message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact/support`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v-container" style={{ paddingTop: 80, paddingBottom: 140, maxWidth: 920 }} data-testid="support-page">
      <Seo
        path="/support"
        title="Support — Verdict"
        description="Get help with Verdict, the AI-powered packaging design audit platform. Email info@thereforedesign.co.in — 24-48 hour response, Mon-Fri 9-5 IST."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            url: "https://verdict.thereforedesign.co.in/support",
            name: "Verdict Support",
            about: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
          },
        ]}
      />
      <div className="overline" style={{ color: "var(--v-accent)" }}>Support</div>
      <h1 style={{ fontSize: 48, marginTop: 12, lineHeight: 1.08, letterSpacing: "-.02em" }}>
        Talk to a human at Therefore Design.
      </h1>
      <p style={{ color: "var(--v-text-2)", marginTop: 18, maxWidth: 660, lineHeight: 1.65, fontSize: 16 }}>
        Bugs, billing, audit accuracy, feature ideas, partnership inquiries — write to us
        and we&apos;ll reply within <b style={{ color: "var(--v-text)" }}>24 to 48 working hours</b>.
      </p>

      <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <InfoCard label="Working hours" value="Mon - Fri · 9 AM - 5 PM IST" />
        <InfoCard label="Response SLA" value="24 - 48 working hours" />
        <InfoCard
          label="Direct inbox"
          value={
            <a href="mailto:info@thereforedesign.co.in" style={{ color: "var(--v-accent)", textDecoration: "none" }} data-testid="support-direct-email">
              info@thereforedesign.co.in
            </a>
          }
        />
      </div>

      <div style={{ marginTop: 48 }}>
        {submitted ? (
          <div className="v-surface" style={{ padding: 36, borderColor: "var(--v-accent)" }} data-testid="support-success">
            <div className="overline" style={{ color: "var(--v-accent)" }}>Message received</div>
            <h2 style={{ fontSize: 28, marginTop: 10 }}>Thanks {form.name.split(" ")[0]} — we&apos;re on it.</h2>
            <p style={{ color: "var(--v-text-2)", marginTop: 14, maxWidth: 540, fontSize: 14, lineHeight: 1.6 }}>
              An acknowledgement just landed in your inbox. A real human from the
              studio will follow up within <b>24-48 working hours</b>. If anything
              urgent comes up before then, write to{" "}
              <a href="mailto:info@thereforedesign.co.in" style={{ color: "var(--v-accent)" }}>info@thereforedesign.co.in</a>.
            </p>
            <button
              className="v-btn v-btn-ghost"
              onClick={() => { setSubmitted(false); setForm({ name: "", email: "", topic: TOPICS[0], message: "" }); }}
              data-testid="support-send-another-btn"
              style={{ marginTop: 24 }}
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="v-surface" style={{ padding: 36 }} data-testid="support-form">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
              <Field label="Your name" required>
                <input
                  className="v-input" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="support-name"
                />
              </Field>
              <Field label="Your email" required>
                <input
                  className="v-input" required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="support-email"
                />
              </Field>
            </div>
            <Field label="Topic" style={{ marginTop: 18 }}>
              <select
                className="v-input"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                data-testid="support-topic"
                style={{ width: "100%" }}
              >
                {TOPICS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Your message" required style={{ marginTop: 18 }}>
              <textarea
                className="v-input" required rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what's going on. The more context the faster we can help."
                data-testid="support-message"
                style={{ width: "100%", resize: "vertical", minHeight: 140 }}
              />
            </Field>
            <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--v-text-2)" }}>
                You&apos;ll get an acknowledgement email immediately.
              </span>
              <button className="v-btn" type="submit" disabled={busy} data-testid="support-submit-btn">
                {busy ? "Sending…" : "Send message →"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children, style }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 10, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".14em" }}>
        {label}{required && <span style={{ color: "var(--v-accent)" }}> *</span>}
      </label>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="v-surface" style={{ padding: "18px 20px" }}>
      <div style={{ fontSize: 10, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".14em" }}>{label}</div>
      <div style={{ fontSize: 15, marginTop: 6, color: "var(--v-text)" }}>{value}</div>
    </div>
  );
}
